/**
 * Enumerations shared by several screens.
 *
 * These used to be re-declared in every page that needed them (four copies of
 * the customer status list alone). They mirror the backend enums - the API
 * remains the authority and rejects anything it does not know, so a drift shows
 * up as a validation error rather than silent bad data.
 */
export const CUSTOMER_STATUSES = [
  'NEW',
  'CONTACTED',
  'QUALIFIED',
  'MEETING_BOOKED',
  'PROPOSAL_SENT',
  'NEGOTIATION',
  'WON',
  'LOST',
  'ON_HOLD',
];

export const CUSTOMER_SOURCES = [
  'COLD_CALL',
  'INBOUND',
  'REFERRAL',
  'LINKEDIN',
  'EVENT',
  'PARTNER',
  'WEBSITE',
  'IMPORT',
  'OTHER',
];

export const LEAD_STATUSES = ['DRAFT', 'SUBMITTED', 'MEETING_BOOKED', 'CONVERTED', 'REJECTED'];
export const TEMPERATURES = ['HOT', 'WARM', 'COLD'];
export const COMPLEXITIES = ['LOW', 'MEDIUM', 'HIGH'];

export const APPOINTMENT_STATUSES = ['SCHEDULED', 'CONFIRMED', 'DONE', 'CANCELLED', 'NO_SHOW'];
export const APPOINTMENT_CHANNELS = ['VIDEO', 'PHONE', 'ONSITE'];
export const DURATIONS = [30, 45, 60, 90, 120];

export const USER_ROLES = ['TELEPRO', 'COMMERCIAL', 'AGENCY', 'IT', 'ADMIN'];
export const AGENCY_ROLES = ['TELEPRO', 'COMMERCIAL', 'AGENCY'];

export const DOCUMENT_TYPES = ['QUOTE', 'INVOICE', 'RECEIPT', 'CREDIT_NOTE'];
export const DOCUMENT_STATUSES = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'REFUSED',
  'EXPIRED',
  'PARTIAL',
  'PAID',
  'LATE',
  'CANCELLED',
];

export const TASK_STATUSES = ['OPEN', 'DONE', 'CANCELLED'];
export const TASK_PRIORITIES = ['LOW', 'NORMAL', 'HIGH'];

/**
 * Delivery projects. THE ORDER IS THE ORDER OF THE COLUMNS on the board, and
 * it mirrors PROJECT_STATUS in the API - the board reads left to right and a
 * card moves one column at a time.
 *
 * `OPEN_PROJECT_STATUSES` mirrors OPEN_STATUS: four of the six mean "still on
 * somebody's plate", and the dashboard tiles count on the same split.
 */
export const PROJECT_STATUSES = ['TO_DO', 'DEVELOPING', 'QA', 'READY', 'DELIVERED', 'CANCELLED'];
export const OPEN_PROJECT_STATUSES = ['TO_DO', 'DEVELOPING', 'QA', 'READY'];
export const isProjectOpen = (status) => OPEN_PROJECT_STATUSES.includes(status);
export const PROJECT_PRIORITIES = ['LOW', 'NORMAL', 'HIGH'];

/**
 * Roles that only ever work their own list - mirror of SELF_ONLY_ROLES in the
 * API. Every "assign to" control is dropped for them: teleprospection creates
 * prospects, qualifications and tasks for itself. Its one hand-over is passing
 * a finished qualification to a sales person, which happens when the meeting is
 * booked, not through a dropdown.
 */
export const SELF_ONLY_ROLES = ['TELEPRO'];
export const worksAlone = (role) => SELF_ONLY_ROLES.includes(role);

/** Naming an owner on a customer file. Matches CUSTOMER_ASSIGN in the API. */
export const CAN_ASSIGN_ROLES = ['ADMIN', 'AGENCY'];

/**
 * Marking a deal signed, which hands the account to the IT team. Matches
 * CUSTOMER_SIGN in the API, which additionally checks that a sales rep owns the
 * customer in question - this list only decides whether the control is worth
 * drawing at all.
 */
export const CAN_SIGN_ROLES = ['COMMERCIAL', 'ADMIN'];

/** Roles whose screens are about delivery rather than about the funnel. */
export const DELIVERY_ROLES = ['IT', 'ADMIN'];

/** Roles that quote and invoice. Mirrors DOCUMENT_MANAGE in the API. */
export const CAN_BILL_ROLES = ['COMMERCIAL', 'ADMIN'];

/**
 * Colour slots used to tell people apart in the calendar. Not a data-viz
 * palette - see components/charts/palette.js for that one.
 */
export const PERSON_TONES = ['violet', 'green', 'blue', 'amber', 'indigo', 'red'];
